import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleHalfStroke } from '@fortawesome/pro-light-svg-icons/faCircleHalfStroke';
import { faMoon } from '@fortawesome/pro-light-svg-icons/faMoon';
import { faSun } from '@fortawesome/pro-light-svg-icons/faSun';
import { TypedMemo } from '../../common/types';
import { useSMLTheme } from '../theme/useSMLTheme';
import { SML_THEME_PREFERENCES, SMLThemePreference } from '../theme/smlTheme';
import styles from './SMLThemeSwitch.module.scss';

/** Where the menu opens. `auto` opens it upward when the space below the button is too small. */
export type SMLThemeMenuDirection = 'auto' | 'down' | 'up';

interface SMLThemeSwitchProps {
  /**
   * `icon`: a 34 px square button with only the icon of the current choice. `row`: the icon and
   * "Theme: <choice>". The design is the theme control of music24.com (website v2 ThemeSwitch).
   */
  variant?: 'icon' | 'row';
  direction?: SMLThemeMenuDirection;
  /** The menu edge that lines up with the button. The default: `end` for `icon`, `start` for `row`. */
  align?: 'start' | 'end';
}

const OPTION_ICONS: Record<SMLThemePreference, IconDefinition> = {
  system: faCircleHalfStroke,
  light: faSun,
  dark: faMoon,
};

// The height of the menu with three items, plus the gap to the button.
const MENU_ROOM_PX = 140;

function opensUpward(button: HTMLElement | null, direction: SMLThemeMenuDirection): boolean {
  if (direction !== 'auto') {
    return direction === 'up';
  }
  if (button === null || typeof window === 'undefined') {
    return false;
  }
  return window.innerHeight - button.getBoundingClientRect().bottom < MENU_ROOM_PX;
}

const SMLThemeSwitchComponent: React.FC<SMLThemeSwitchProps> = ({ variant = 'icon', direction = 'auto', align }) => {
  const intl = useIntl();
  const { preference, setPreference } = useSMLTheme();
  const [isOpen, setOpen] = useState(false);
  const [isUp, setUp] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const labels = useMemo<Record<SMLThemePreference, string>>(
    () => ({
      system: intl.formatMessage({ id: 'app.sml.sidebar.theme.system', defaultMessage: 'System' }),
      light: intl.formatMessage({ id: 'app.sml.sidebar.theme.light', defaultMessage: 'Light' }),
      dark: intl.formatMessage({ id: 'app.sml.sidebar.theme.dark', defaultMessage: 'Dark' }),
    }),
    [intl]
  );
  const currentLabel = labels[preference];
  const buttonLabel = intl.formatMessage(
    { id: 'app.sml.sidebar.theme.button', defaultMessage: 'Theme: {name}' },
    { name: currentLabel }
  );

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) {
      buttonRef.current?.focus();
    }
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close(false);
      return;
    }
    setUp(opensUpward(buttonRef.current, direction));
    setOpen(true);
  }, [isOpen, close, direction]);

  const pick = useCallback(
    (next: SMLThemePreference) => {
      setPreference(next);
      close(true);
    },
    [setPreference, close]
  );

  // The focus goes to the current choice when the menu opens. A pick closes the menu, so a change
  // of the choice never moves the focus inside an open menu.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    itemRefs.current[Math.max(0, SML_THEME_PREFERENCES.indexOf(preference))]?.focus();
  }, [isOpen, preference]);

  // A click outside the control closes the menu. The focus stays where the user clicked.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const { target } = event;
      if (target instanceof Node && rootRef.current?.contains(target) === true) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen]);

  const onMenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const items = itemRefs.current;
      const count = SML_THEME_PREFERENCES.length;
      const focused = items.findIndex((item) => item !== null && item === document.activeElement);
      let next: number;
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          close(true);
          return;
        case 'Tab':
          close(false);
          return;
        case 'ArrowDown':
        case 'ArrowRight':
          next = (focused + 1) % count;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          next = (Math.max(focused, 0) - 1 + count) % count;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = count - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      items[next]?.focus();
    },
    [close]
  );

  const onButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        close(true);
      }
    },
    [isOpen, close]
  );

  const menuAlign = align ?? (variant === 'row' ? 'start' : 'end');

  return (
    <div ref={rootRef} className={styles.themeControl}>
      <button
        ref={buttonRef}
        type="button"
        className={clsx(styles.button, variant === 'row' ? styles.row : styles.icon, isOpen && styles.on)}
        aria-label={buttonLabel}
        title={buttonLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={toggle}
        onKeyDown={onButtonKeyDown}
      >
        <FontAwesomeIcon icon={OPTION_ICONS[preference]} />
        {variant === 'row' ? (
          <span className={styles.label} aria-hidden="true">
            {intl.formatMessage(
              { id: 'app.sml.sidebar.theme.button', defaultMessage: 'Theme: {name}' },
              { name: <b key="name">{currentLabel}</b> }
            )}
          </span>
        ) : null}
      </button>
      {isOpen ? (
        <div
          id={menuId}
          className={clsx(styles.menu, isUp && styles.up, menuAlign === 'start' ? styles.alignStart : styles.alignEnd)}
          role="menu"
          tabIndex={-1}
          aria-label={intl.formatMessage({ id: 'app.sml.sidebar.theme.label', defaultMessage: 'Color theme' })}
          onKeyDown={onMenuKeyDown}
        >
          {SML_THEME_PREFERENCES.map((option, index) => {
            const isCurrent = option === preference;
            return (
              <button
                key={option}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                tabIndex={isCurrent ? 0 : -1}
                className={clsx(styles.item, isCurrent && styles.current)}
                onClick={() => pick(option)}
              >
                <FontAwesomeIcon icon={OPTION_ICONS[option]} fixedWidth />
                <span className={styles.itemLabel}>{labels[option]}</span>
                <span className={styles.check} aria-hidden="true">
                  {isCurrent ? <FontAwesomeIcon icon={faCheck} /> : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export const SMLThemeSwitch = TypedMemo(SMLThemeSwitchComponent);
